import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  Reducer,
} from "@reduxjs/toolkit";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import {
  BodyGetPurchasingGuideFromCustomMenusOnDatesInventoryPurchasingFromCustomMenusOnDatesPost,
  BodyGetPurchasingGuideFromPortionOrdersInventoryPurchasingFromPortionOrdersPost,
  ParamFilterPurchaseGuide,
  PurchasingGuide,
  PurchasingGuideItem,
} from "client/jspPlatformExperiment";

interface FoodPurchasingGuideStateProps {
  data: PurchasingGuideItem[] | null;
  filterParams?: ParamFilterPurchaseGuide | null;
  isLoading: boolean;
  error: string | null;
  warnings: string | null;
}

const initialState: FoodPurchasingGuideStateProps = {
  data: [],
  filterParams: null,
  isLoading: false,
  error: null,
  warnings: null,
};

const fetchFoodPurchasingGuideByMenusOnDate = createAsyncThunk(
  "foodPurchasingGuide/fetchByMenusOnDate",
  async (
    input: BodyGetPurchasingGuideFromCustomMenusOnDatesInventoryPurchasingFromCustomMenusOnDatesPost,
    { rejectWithValue },
  ) => {
    return jspPlatformApi()
      .purchasing.postInventoryPurchasingFromCustomMenusOnDates({
        body: input,
      })
      .then((res) => res.data ?? null)
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      });
  },
);

const fetchFoodPurchasingGuide = createAsyncThunk(
  "foodPurchasingGuide/fetchByDishPortionOrders",
  async (
    input: BodyGetPurchasingGuideFromPortionOrdersInventoryPurchasingFromPortionOrdersPost,
    { signal, rejectWithValue },
  ) => {
    return jspPlatformApi()
      .purchasing.postInventoryPurchasingFromPortionOrders({
        body: input,
        signal,
      })
      .then((res) => res.data ?? null)
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      });
  },
);

const updateFoodPurchasingGuide = (
  state: FoodPurchasingGuideStateProps,
  action: PayloadAction<PurchasingGuide | null>,
): FoodPurchasingGuideStateProps => {
  return {
    ...state,
    data: action.payload?.data ?? [],
    warnings: action.payload?.warnings ?? null,
  };
};

const clearFoodPurchasingGuideWarning = (
  state: FoodPurchasingGuideStateProps,
): FoodPurchasingGuideStateProps => {
  return {
    ...state,
    warnings: null,
  };
};

export const foodPurchasingGuideSlice = createSlice({
  name: "foodPurchasingGuideSlice",
  initialState,
  reducers: {
    setFoodPurchasingGuide: updateFoodPurchasingGuide,
    clearFoodPurchasingGuideWarning,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFoodPurchasingGuide.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchFoodPurchasingGuide.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateFoodPurchasingGuide(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchFoodPurchasingGuide.rejected, (state, action) => {
      return { ...state, isLoading: false, error: action.payload as string };
    });
    builder.addCase(fetchFoodPurchasingGuideByMenusOnDate.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(
      fetchFoodPurchasingGuideByMenusOnDate.fulfilled,
      (state, action) => {
        return {
          ...state,
          ...updateFoodPurchasingGuide(state, action),
          isLoading: false,
        };
      },
    );
    builder.addCase(
      fetchFoodPurchasingGuideByMenusOnDate.rejected,
      (state, action) => {
        return { ...state, isLoading: false, error: action.payload as string };
      },
    );
  },
});

export const foodPurchasingGuideActions = {
  ...foodPurchasingGuideSlice.actions,
  fetchFoodPurchasingGuide,
  fetchFoodPurchasingGuideByMenusOnDate,
};

export const FoodPurchaseGuideReducer: Reducer<FoodPurchasingGuideStateProps> =
  foodPurchasingGuideSlice.reducer;

export default FoodPurchaseGuideReducer;
